import {
  getMergeFieldDefinition,
  type MergeFieldBinding,
  type MergeFieldDefinition,
} from "./merge-fields"

export type LegacyFieldCategory = "static" | "prefill" | "required" | "optional"

export type FieldRecipientAccess = "readonly" | "required" | "optional"

export type FieldCollectionPhase = "packet_creation" | "sign_time"

export type FieldValueSource = "constant" | "merge" | "senderInput"

export type ChoiceGroupSelectionMode = "single" | "multi"

export type ChoiceGroupValueSource = "documentInput" | "mergeEnum"

export type SelectFieldValueSource = "documentInput" | "mergeEnum"

export type SelectFieldOption = {
  value: string
  label: string
}

export type SelectFieldConfig = {
  options: SelectFieldOption[]
  valueSource: SelectFieldValueSource
  sourceDefinitionKey?: string
}

export type DealLineItemType =
  | "vehicle"
  | "tax"
  | "fee"
  | "service"
  | "part"
  | "labor"
  | "warranty"
  | "gap"
  | "shipping"
  | "trade_allowance"
  | "trade_payoff"
  | "cash_down"
  | "deposit"
  | "payment"
  | "payoff"
  | "discount"
  | "other"

export type LineItemAmountDisplay = "plain" | "signed"

export type LineItemsTableConfig = {
  collection: "vehicle_line_items"
  amountDisplay?: LineItemAmountDisplay
  labelColumnRatio?: number
  maxFontSize?: number
}

export type CheckboxGroupConfig = {
  mode: "single"
  groupKey: string
  optionValue: string
}

export type ChoiceGroupConfig = {
  groupKey: string
  groupLabel?: string
  optionValue: string
  optionLabel?: string
  selectionMode: ChoiceGroupSelectionMode
  valueSource: ChoiceGroupValueSource
}

type FieldSemanticsInput = {
  fieldCategory?: LegacyFieldCategory
  recipientAccess?: FieldRecipientAccess
  signTimeRecipientAccess?: FieldRecipientAccess
  collectionPhase?: FieldCollectionPhase
  valueSource?: FieldValueSource
  mergeFieldBinding?: MergeFieldBinding
  lineItemsTableConfig?: LineItemsTableConfig
}

type CheckboxCapableField = {
  fieldType?: string
  checkboxGroup?: CheckboxGroupConfig
  choiceGroup?: ChoiceGroupConfig
  mergeFieldBinding?: MergeFieldBinding
}

type SelectCapableField = {
  fieldType?: string
  selectConfig?: SelectFieldConfig
  mergeFieldBinding?: MergeFieldBinding
}

export function isCheckboxValueChecked(value: unknown) {
  return value === true || value === "true"
}

export function getFieldChoiceGroup(
  field: CheckboxCapableField | undefined | null,
): ChoiceGroupConfig | undefined {
  if (!field || field.fieldType !== "checkbox") {
    return undefined
  }

  if (field.choiceGroup?.groupKey.trim().length && field.choiceGroup.optionValue.trim().length) {
    return {
      selectionMode: field.choiceGroup.selectionMode,
      valueSource: field.choiceGroup.valueSource,
      groupLabel: field.choiceGroup.groupLabel,
      optionLabel: field.choiceGroup.optionLabel,
      groupKey: field.choiceGroup.groupKey,
      optionValue: field.choiceGroup.optionValue,
    }
  }

  if (
    field.checkboxGroup?.groupKey.trim().length &&
    field.checkboxGroup.optionValue.trim().length
  ) {
    return {
      selectionMode: "single",
      valueSource: "documentInput",
      groupLabel: undefined,
      groupKey: field.checkboxGroup.groupKey,
      optionValue: field.checkboxGroup.optionValue,
    }
  }

  return undefined
}

function normalizeSelectOption(
  option: SelectFieldOption | undefined | null,
): SelectFieldOption | null {
  const value = option?.value?.trim()
  const label = option?.label?.trim()

  if (!value || !label) {
    return null
  }

  return { value, label }
}

export function mergeSelectOptions(
  sourceOptions: SelectFieldOption[] | undefined | null,
  fallbackOptions: SelectFieldOption[] | undefined | null,
): SelectFieldOption[] {
  const mergedOptions = new Map<string, SelectFieldOption>()

  for (const option of sourceOptions ?? []) {
    const normalized = normalizeSelectOption(option)
    if (!normalized) continue
    mergedOptions.set(normalized.value, normalized)
  }

  for (const option of fallbackOptions ?? []) {
    const normalized = normalizeSelectOption(option)
    if (!normalized || mergedOptions.has(normalized.value)) continue
    mergedOptions.set(normalized.value, normalized)
  }

  return [...mergedOptions.values()]
}

export function getFieldSelectConfig(
  field: SelectCapableField | undefined | null,
): SelectFieldConfig | undefined {
  if (!field || field.fieldType !== "select") {
    return undefined
  }

  const normalizedOptions = (field.selectConfig?.options ?? [])
    .map((option) => normalizeSelectOption(option))
    .filter((option): option is SelectFieldOption => option !== null)

  return {
    options: normalizedOptions,
    valueSource: field.selectConfig?.valueSource ?? "documentInput",
    sourceDefinitionKey: field.selectConfig?.sourceDefinitionKey?.trim() || undefined,
  }
}

export function getFieldSelectOptions(
  field: SelectCapableField | undefined | null,
  sourceDefinition?: Pick<MergeFieldDefinition, "enumOptions">,
): SelectFieldOption[] {
  const selectConfig = getFieldSelectConfig(field)
  if (!selectConfig) {
    return []
  }

  const resolvedSourceDefinition =
    sourceDefinition ??
    (selectConfig.sourceDefinitionKey
      ? getMergeFieldDefinition(selectConfig.sourceDefinitionKey)
      : field?.mergeFieldBinding?.definitionKey
        ? getMergeFieldDefinition(field.mergeFieldBinding.definitionKey)
        : undefined)

  if (selectConfig.valueSource !== "mergeEnum" || !resolvedSourceDefinition?.enumOptions?.length) {
    return selectConfig.options
  }

  return mergeSelectOptions(resolvedSourceDefinition.enumOptions, selectConfig.options)
}

export function getFieldSelectOptionsIncludingValue(
  field: SelectCapableField | undefined | null,
  value: string | undefined | null,
  sourceDefinition?: Pick<MergeFieldDefinition, "enumOptions">,
): SelectFieldOption[] {
  const normalizedValue = value?.trim()
  const options = getFieldSelectOptions(field, sourceDefinition)

  if (!normalizedValue || options.some((option) => option.value === normalizedValue)) {
    return options
  }

  return [...options, { value: normalizedValue, label: normalizedValue }]
}

export function getFieldSelectOptionLabel(
  field: SelectCapableField | undefined | null,
  value: string | undefined | null,
  sourceDefinition?: Pick<MergeFieldDefinition, "enumOptions">,
) {
  const normalizedValue = value?.trim()
  if (!normalizedValue) {
    return undefined
  }

  return getFieldSelectOptions(field, sourceDefinition).find(
    (option) => option.value === normalizedValue,
  )?.label
}

export function isSelectField(field: SelectCapableField | undefined | null) {
  return !!getFieldSelectConfig(field)
}

export function isChoiceGroupField(field: CheckboxCapableField | undefined | null) {
  return !!getFieldChoiceGroup(field)
}

export function isSingleSelectChoiceField(field: CheckboxCapableField | undefined | null) {
  const choiceGroup = getFieldChoiceGroup(field)
  return (
    field?.fieldType === "checkbox" &&
    choiceGroup?.selectionMode === "single" &&
    choiceGroup.groupKey.trim().length > 0 &&
    choiceGroup.optionValue.trim().length > 0
  )
}

export function isSingleSelectCheckboxField(field: CheckboxCapableField | undefined | null) {
  return isSingleSelectChoiceField(field)
}

export function applyCheckboxFieldChange<
  TField extends CheckboxCapableField,
  TValue extends boolean | string,
>(
  fields: TField[],
  currentValues: Record<string, TValue>,
  targetField: TField,
  nextChecked: boolean,
  getFieldKey: (field: TField) => string,
  checkedValue: TValue,
  uncheckedValue: TValue,
) {
  const targetKey = getFieldKey(targetField)
  const targetChoiceGroup = getFieldChoiceGroup(targetField)

  if (!targetChoiceGroup || targetChoiceGroup.selectionMode !== "single") {
    return {
      ...currentValues,
      [targetKey]: nextChecked ? checkedValue : uncheckedValue,
    }
  }

  if (!nextChecked) {
    return currentValues
  }

  const nextValues = { ...currentValues }
  const groupKey = targetChoiceGroup.groupKey.trim()

  for (const field of fields) {
    const fieldGroup = getFieldChoiceGroup(field)
    if (!fieldGroup || fieldGroup.groupKey.trim() !== groupKey) continue

    nextValues[getFieldKey(field)] =
      getFieldKey(field) === targetKey ? checkedValue : uncheckedValue
  }

  return nextValues
}

export function bindingNeedsSenderInput(binding?: MergeFieldBinding) {
  if (!binding) return false

  return (
    binding.primarySource === "manual" ||
    binding.primarySource === "dealCustomField" ||
    binding.primarySource === "dealershipCustomField" ||
    binding.requiredAtSend === true ||
    binding.fallback === "manual" ||
    binding.fallbackSources?.includes("manual") === true
  )
}

export function isSenderEnterableChoiceGroup(field: CheckboxCapableField | undefined | null) {
  const choiceGroup = getFieldChoiceGroup(field)
  if (!choiceGroup) {
    return false
  }

  return (
    choiceGroup.valueSource === "mergeEnum" && bindingNeedsSenderInput(field?.mergeFieldBinding)
  )
}

export function getFieldRecipientAccess(field: FieldSemanticsInput): FieldRecipientAccess {
  if (field.recipientAccess) {
    return field.recipientAccess
  }

  switch (field.fieldCategory) {
    case "static":
    case "prefill":
      return "readonly"
    case "optional":
      return "optional"
    case "required":
    default:
      return "required"
  }
}

export function getFieldValueSource(field: FieldSemanticsInput): FieldValueSource | undefined {
  if (field.valueSource) {
    return field.valueSource
  }

  if (field.lineItemsTableConfig) {
    return "merge"
  }

  switch (field.fieldCategory) {
    case "static":
      return field.mergeFieldBinding ? "merge" : "constant"
    case "prefill":
      return bindingNeedsSenderInput(field.mergeFieldBinding) ? "senderInput" : "merge"
    default:
      return undefined
  }
}

export function getFieldCollectionPhase(
  field: FieldSemanticsInput,
): FieldCollectionPhase | undefined {
  const recipientAccess = getFieldRecipientAccess(field)
  if (recipientAccess === "readonly") {
    return undefined
  }

  return field.collectionPhase ?? "sign_time"
}

export function isPacketCreationField(field: FieldSemanticsInput) {
  return getFieldCollectionPhase(field) === "packet_creation"
}

export function isSignTimeField(field: FieldSemanticsInput) {
  return getFieldCollectionPhase(field) === "sign_time"
}

export function getSignTimeRecipientAccess(field: FieldSemanticsInput): FieldRecipientAccess {
  const recipientAccess = getFieldRecipientAccess(field)

  if (recipientAccess !== "readonly" && getFieldCollectionPhase(field) === "packet_creation") {
    return field.signTimeRecipientAccess ?? "readonly"
  }

  return recipientAccess
}

export function isReadonlyField(field: FieldSemanticsInput) {
  return getFieldRecipientAccess(field) === "readonly"
}

export function isCustomerRequiredField(field: FieldSemanticsInput) {
  return getFieldRecipientAccess(field) === "required"
}

export function isMergeResolvedField(field: FieldSemanticsInput) {
  return getFieldValueSource(field) === "merge"
}

export function isSenderInputField(field: FieldSemanticsInput) {
  return getFieldValueSource(field) === "senderInput"
}

export function usesMergeBinding(field: FieldSemanticsInput) {
  const valueSource = getFieldValueSource(field)
  return !!field.mergeFieldBinding && (valueSource === "merge" || valueSource === "senderInput")
}

export function getLegacyFieldCategory(field: FieldSemanticsInput): LegacyFieldCategory {
  if (field.fieldCategory && !field.recipientAccess && !field.valueSource) {
    return field.fieldCategory
  }

  const recipientAccess = getFieldRecipientAccess(field)
  const valueSource = getFieldValueSource(field)

  if (recipientAccess === "required") return "required"
  if (recipientAccess === "optional") return "optional"
  if (valueSource === "senderInput") return "prefill"
  if (valueSource === "merge" && bindingNeedsSenderInput(field.mergeFieldBinding)) {
    return "prefill"
  }

  return "static"
}
